<script lang="ts" setup>
interface LegendPillItem {
  key: string;
  label: string;
  active?: boolean;
  color?: string;
  swatchStyle?: Record<string, string>;
}
interface Props {
  items: LegendPillItem[];
  interactive?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  interactive: false,
});
const emit = defineEmits<{
  toggle: [key: string];
}>();
function onToggle(item: LegendPillItem) {
  if (!props.interactive) {
    return;
  }
  emit('toggle', item.key);
}
function onKeydown(event: KeyboardEvent, item: LegendPillItem) {
  if (!props.interactive) {
    return;
  }
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onToggle(item);
  }
}
const getSwatchStyle = (item: LegendPillItem) =>
  item.swatchStyle ?? (item.color ? { backgroundColor: item.color } : undefined);
</script>

<template>
  <div :class="$style.root">
    <div
      v-for="item in props.items"
      :key="item.key"
      :class="[$style.item, item.active === false && $style.itemDisabled]"
      :role="props.interactive ? 'button' : undefined"
      :tabindex="props.interactive ? 0 : undefined"
      @click="onToggle(item)"
      @keydown="onKeydown($event, item)"
    >
      <span :class="$style.swatch" :style="getSwatchStyle(item)" />
      <span :class="$style.label">{{ item.label }}</span>
    </div>
  </div>
</template>

<style lang="scss" module>
.root {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  align-items: center;
  padding-left: 0;
}

.item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.item:hover {
  background-color: rgba(1, 0, 0, 0.06);
}

.itemDisabled {
  color: var(--ui2-content-secondary);
  text-decoration: line-through;
}

.swatch {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex: 0 0 10px;
}

.label {
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ui2-content-secondary);
}
</style>
